import React, { useEffect, useState } from 'react'

import { gql, useMutation, useQuery } from '@apollo/client'
import axios from 'axios'
import { Field, Formik } from 'formik'
import getConfig from 'next/config'

const { publicRuntimeConfig } = getConfig()
const { version, appUrl } = publicRuntimeConfig

import Button from '@shiphero/ui/Button'
import Input from '@shiphero/ui/Form/Input'

import { redirect } from 'lib/redirect'
import usePusher from 'lib/usePusher'

import { printerServiceUrl } from 'env'
import logo from '../../assets/logo.svg'
import stations from '../../assets/stations-draw.svg'
import useAppSync from '../../lib/useAppSync'
import { useAuth0Token } from '../auth0ApiProvider'
import { Computer, Form, Header, Name, Section } from './styles'

const TEST_PRINT_SERVICE_URL = 'https://printer-service-b.shiphero.com/graphql'

const getVersion = async () => {
  try {
    const response = await axios.get(`${appUrl}/version`)
    const latestVersion = response.data

    if (version !== latestVersion) {
      window.location.reload()
    }
  } catch (e) {
    console.log('Error fetching app version:', e)
  }
}

const useAccountFeatureFlag = () => {
  const GET_USER_PREFERENCES = gql`
    query UserPreferences {
      preferences {
        account_features {
          flag_name
          flag_value
        }
      }
    }
  `

  const { data, loading, error } = useQuery(GET_USER_PREFERENCES)

  const flags = data?.preferences?.account_features

  return {
    data: flags,
    loading,
    error,
  }
}

const useComputer = ipc => {
  const hash = ipc.sendSync('machine-id')

  const ComputerFragment = gql`
    fragment ComputerFragment on Computer {
      id
      created_at
      name
      row_id
      computer_hash
      account_id
      app_version
      printer_stations {
        edges {
          node {
            id
            row_id
            name
            label_printer
            invoice_printer
            barcode_printer
            scale
          }
        }
      }
      printers {
        edges {
          node {
            id
            row_id
            name
          }
        }
      }
      usb_devices {
        edges {
          node {
            id
            row_id
            name
            value
          }
        }
      }
    }
  `

  const [createComputer] = useMutation(gql`
    ${ComputerFragment}
    mutation CreateComputer($data: CreateComputerInput!) {
      create_computer(data: $data) {
        computer {
          ...ComputerFragment
        }
      }
    }
  `)

  // TODO: we can probably search for the hash directly
  const { data, loading, error, refetch } = useQuery(
    gql`
      ${ComputerFragment}
      query GetComputers($search: GenericScalar) {
        computers(search: $search) {
          edges {
            node {
              ...ComputerFragment
            }
          }
        }
      }
    `,
    { variables: { search: { computer_hash: hash } } },
  )

  if (error || loading) {
    return { computer: null, loading, error, refetch }
  }

  if (data.computers.edges.length < 1) {
    console.log('Did not find computer, creating one.')
    const hw = ipc.sendSync('get-hardware')
    const { data, error } = createComputer({
      variables: {
        data: {
          ...hw,
          app_version: version,
        },
      },
    })
      .then(() => refetch())
      .catch(e => console.error(e))

    return { computer: data && data.computer, loading, error, refetch }
  }

  return { computer: data.computers.edges[0].node, loading, error, refetch }
}

let ipc = null
if (typeof window !== 'undefined') {
  ipc = window.ipc
}

const logout = () => {
  console.log('Logging out')
  redirect(null, `/logout`)
}

const useReactor = ({
  token,
  computer,
  refetchComputer,
  updateComputerFull,
  updateComputerVersion,
  logout,
  printerServiceUrl,
}) => {
  useEffect(() => {
    if (!ipc) return

    ipc.on('logout', () => {
      logout()
    })

    ipc.on('print-file-error', () => {
      new Notification('ShipHero', {
        body: 'There was a problem printing.',
      })
    })

    const goOnline = async () => {
      ipc.send('online')
    }
    const goOffline = async () => {
      ipc.send('offline')
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', goOnline)
      window.addEventListener('offline', goOffline)
    }
    return () => {
      ipc.removeAllListeners('logout')
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipc])

  if (computer && computer.app_version !== version) {
    console.log('Updating computer to the latest version')
    // We only update the version so the station assign doesn't get
    // messed up when you update
    updateComputerVersion()
  }

  usePusher(
    computer,
    ipc,
    token,
    {
      computer_hash: computer?.computer_hash,
    },
    logout,
  )

  const { computerUpdated: appSyncComputerUpdated } = useAppSync(
    ipc,
    token,
    computer,
    logout,
    updateComputerFull,
    refetchComputer,
    printerServiceUrl,
  )

  return {
    computerUpdated: () => {
      appSyncComputerUpdated().catch(e => {
        console.log('Error ComputerUpdated AppSync:', e)
      })
    },
  }
}

if (typeof window !== 'undefined') {
  window.onerror = a => {
    window.ipc.send('exception', a)
  }
}

const Main = ({ token }) => {
  const { computer, refetch } = useComputer(ipc, version)

  useEffect(() => {
    const interval = setInterval(getVersion, 1000 * 60 * 60) // every hour
    return () => clearInterval(interval)
  }, [])

  const [editing, setEditing] = useState(false)

  console.log('Computer:', computer)

  const {
    data: featureFlag,
    loading: ffLoading,
    error: ffError,
  } = useAccountFeatureFlag()

  const getPrintServiceUrl = () => {
    //To stop creating AppSync client & subscribe with wrong url
    if (ffLoading || !featureFlag) return null

    const testPrintServiceEnabled = featureFlag?.some(
      ff =>
        ff.flag_name === 'test_new_printer_service' && ff.flag_value === '1',
    )

    if (ffError) {
      console.error('Error on fetching account preferences:', ffError)
    }

    if (testPrintServiceEnabled) return TEST_PRINT_SERVICE_URL

    return printerServiceUrl
  }

  const [updateComputer, { loading: updateComputerLoading }] = useMutation(
    gql`
      mutation UpdateComputer($data: UpdateComputerInput!) {
        update_computer(data: $data) {
          computer {
            row_id
            name
          }
        }
      }
    `,
  )

  const [deleteComputer] = useMutation(
    gql`
      mutation DeleteComputer($row_id: Int!) {
        delete_computer(row_id: $row_id) {
          ok
        }
      }
    `,
  )

  const updateComputerFull = async (name = computer.name) => {
    const hw = ipc.sendSync('get-hardware')
    const { error } = await updateComputer({
      variables: {
        data: {
          ...hw,
          app_version: version,
          computer_hash: computer.computer_hash,
          row_id: computer.row_id,
          name: name,
        },
      },
    })
    if (error) {
      console.log('Error updating computer', hw, error)
    }

    refetch()
  }

  const updateComputerVersion = async () => {
    const { error } = await updateComputer({
      variables: {
        data: {
          app_version: version,
          row_id: computer.row_id,
        },
      },
    })
    if (error) {
      console.log('Error updating computer version', error)
    }

    refetch()
  }

  const { computerUpdated } = useReactor({
    token,
    computer,
    refetchComputer: refetch,
    updateComputerFull,
    updateComputerVersion,
    logout,
    printerServiceUrl: getPrintServiceUrl(),
  })

  const isLoading =
    !computer || !computer.name || updateComputerLoading || ffLoading

  return (
    <>
      <Header>
        <img src={logo} />
        <span>{`v${version}`}</span>
      </Header>
      <Computer>
        <h1>This computer is registered as:</h1>
        {!editing ? (
          <Name
            onClick={() => {
              computer && setEditing(!editing)
            }}>
            {isLoading ? '...' : computer.name}
          </Name>
        ) : (
          <Formik
            initialValues={computer}
            onSubmit={async a => {
              updateComputerFull(a.name)
              await computerUpdated()
              setEditing(false)
            }}
            render={({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Field name="name" component={Input} />
                <Button
                  onClick={() => {
                    setEditing(false)
                  }}
                  label="Cancel"
                  type="button"
                />
                <Button kind="primary" label="Save" type="submit" />
              </Form>
            )}
          />
        )}
        <p>
          If you would like to unregister this computer, click{' '}
          <a
            onClick={async () => {
              if (!computer) return
              await deleteComputer({ variables: { row_id: computer.row_id } })
              logout()
            }}>
            here
          </a>
        </p>
      </Computer>
      <Section>
        <img src={stations} />
        <p>
          Need to add new or modify <br /> your current printers and scales?
        </p>
        <Button
          kind="primary"
          label="Go to ShipHero"
          onClick={() => ipc.send('open-external', 'stations')}
        />
      </Section>
    </>
  )
}

const AuthorizedMain = () => {
  const auth0Token = useAuth0Token()
  const [token, setToken] = useState(null)

  // If using auth0 a token will be available replace and set this as the token
  useEffect(() => {
    setToken(auth0Token)
  }, [auth0Token])

  useEffect(() => {
    return () => {
      if (ipc) {
        ipc.removeAllListeners('logout')
      }
    }
  }, [])

  // Only load the Main component if we have a token to avoid error with AppSync, Pusher and other token sentivie services
  return token ? <Main token={token} /> : null
}

export default AuthorizedMain
